import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { stashPendingMetaPermissionError, clearPendingMetaPermissionErrorStorage } from 'utils/metaPermissionErrors';

const DEFAULT_PERMISSION_TITLE = 'Faltan permisos para usar Meta Ads';

const parseMissingPermissionsParam = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
    return decoded
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
};

const MetaOAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Conectando con Meta Ads...');

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tokenUpdated = query.get('token_updated');
    const errorParam = query.get('error');

    if (tokenUpdated === 'true') {
      clearPendingMetaPermissionErrorStorage();
      setStatus('success');
      setMessage('Meta Ads conectado correctamente.');

      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'META_AUTH_SUCCESS',
            timestamp: Date.now(),
          },
          window.location.origin
        );
      } else {
        setTimeout(() => navigate('/home', { replace: true, state: { metaConnected: true } }), 2000);
      }
      return;
    }

    if (errorParam === 'meta_permissions_incomplete') {
      const missingPermissions = parseMissingPermissionsParam(query.get('missing_permissions') || '');
      const payload = {
        message: DEFAULT_PERMISSION_TITLE,
        missingPermissions,
        source: 'oauth_callback',
        errorCode: 'meta_permissions_incomplete',
      };
      stashPendingMetaPermissionError(payload);

      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'META_AUTH_PERMISSION_ERROR',
            payload,
            timestamp: Date.now(),
          },
          window.location.origin
        );
      } else {
        setTimeout(() => navigate('/home', { replace: true }), 100);
      }
      setStatus('error');
      setMessage(DEFAULT_PERMISSION_TITLE);
      return;
    }

    if (errorParam === 'meta_permissions_check_failed') {
      let decoded = 'No pudimos verificar los permisos de Meta. Volvé a conectar e intentá de nuevo.';
      const em = query.get('error_message');
      if (em) {
        try {
          decoded = decodeURIComponent(em.replace(/\+/g, ' '));
        } catch {
          decoded = em;
        }
      }
      const payload = {
        message: decoded,
        missingPermissions: [],
        source: 'oauth_callback',
        errorCode: 'meta_permissions_check_failed',
      };
      stashPendingMetaPermissionError(payload);

      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'META_AUTH_PERMISSION_ERROR',
            payload,
            timestamp: Date.now(),
          },
          window.location.origin
        );
      } else {
        setTimeout(() => navigate('/home', { replace: true }), 100);
      }
      setStatus('error');
      setMessage(decoded);
      return;
    }

    if (errorParam) {
      let errorMessage = 'Error al conectar con Meta Ads.';

      if (errorParam === 'user_not_found') {
        errorMessage = 'Usuario no encontrado. Por favor, verifica tus credenciales.';
      } else {
        try {
          errorMessage = decodeURIComponent(errorParam.replace(/\+/g, ' '));
        } catch {
          errorMessage = errorParam;
        }
      }

      setStatus('error');
      setMessage(errorMessage);

      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'META_AUTH_FAILURE',
            timestamp: Date.now(),
          },
          window.location.origin
        );
      } else {
        setTimeout(() => navigate('/home', { replace: true, state: { metaError: errorMessage } }), 3000);
      }
      return;
    }

    setStatus('loading');
    setMessage('Procesando autenticación...');
  }, [location, navigate]);

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <>
          <h2 className="text-xl font-semibold text-gray-800">Procesando autenticación...</h2>
          <p className="mt-2 text-gray-600">Por favor, espera un momento.</p>
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <h2 className="text-xl font-semibold text-green-600">Éxito</h2>
          <p className="mt-2 text-gray-700">{message}</p>
        </>
      );
    }

    return (
      <>
        <h2 className="text-xl font-semibold text-red-600">Error</h2>
        <p className="mt-2 text-gray-700">{message}</p>
      </>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center px-4">{renderContent()}</div>
    </div>
  );
};

export default MetaOAuthCallback;
